import * as React from "react";
import { DayPicker } from "react-day-picker";
import { es } from "date-fns/locale";
import { Calendar, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import dayjs from "dayjs";
import "react-day-picker/dist/style.css";

const MESES_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const DIAS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

const formatCaptionEs = (date) => `${MESES_ES[date.getMonth()]} ${date.getFullYear()}`;
const formatWeekdayEs = (date) => DIAS_ES[date.getDay()];

const useIsMobile = (breakpoint = 640) => {
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const onChange = () => setIsMobile(mql.matches);
    onChange();
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [breakpoint]);
  return isMobile;
};

/**
 * DateRangePicker
 * value: [dayjs|null, dayjs|null]
 * onChange: ([dayjs|null, dayjs|null]) => void
 * format: string (dayjs format, default "YYYY-MM-DD")
 * allowClear: boolean
 */
const DateRangePicker = ({
  value,
  onChange,
  format: dateFormat = "YYYY-MM-DD",
  allowClear = true,
  disabled = false,
  className,
  style,
}) => {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();

  const from = value?.[0]?.toDate?.() ?? undefined;
  const to = value?.[1]?.toDate?.() ?? undefined;

  const selected = { from, to };

  const handleSelect = (range) => {
    if (!range) {
      onChange?.([null, null]);
      return;
    }
    const f = range.from ? dayjs(range.from) : null;
    const t = range.to ? dayjs(range.to) : null;
    onChange?.([f, t]);
    if (range.from && range.to) {
      setOpen(false);
    }
  };

  const displayText =
    from && to
      ? `${dayjs(from).format(dateFormat)} — ${dayjs(to).format(dateFormat)}`
      : from
      ? `${dayjs(from).format(dateFormat)} — …`
      : "Seleccionar rango";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          style={style}
          className={cn(
            "flex h-9 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-700 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-50 transition-colors whitespace-nowrap",
            !from && !to && "text-gray-400",
            className
          )}
        >
          <Calendar className="h-4 w-4 text-gray-400 shrink-0" />
          <span>{displayText}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto max-w-[calc(100vw-2rem)] p-0" align="start" sideOffset={4}>
        {/* Resumen Desde/Hasta: siempre visible mientras se elige el rango */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-gray-100 px-3 py-2 text-xs">
          <span className="flex items-center gap-1">
            <span className="font-semibold text-gray-500">Desde:</span>
            <span className={cn("font-medium", from ? "text-gray-900" : "text-gray-300")}>
              {from ? dayjs(from).format("DD-MM-YYYY") : "—"}
            </span>
          </span>
          <span className="flex items-center gap-1">
            <span className="font-semibold text-gray-500">Hasta:</span>
            <span className={cn("font-medium", to ? "text-gray-900" : "text-gray-300")}>
              {to ? dayjs(to).format("DD-MM-YYYY") : "—"}
            </span>
          </span>
        </div>

        <DayPicker
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          locale={es}
          numberOfMonths={isMobile ? 1 : 2}
          showOutsideDays
          formatters={{
            formatCaption: formatCaptionEs,
            formatWeekdayName: formatWeekdayEs,
          }}
          classNames={{
            months: "flex flex-col sm:flex-row gap-4 p-3",
            month: "space-y-3",
            caption: "flex justify-center relative items-center h-9",
            caption_label: "text-base font-bold text-gray-900 tracking-wide",
            nav: "flex items-center gap-1",
            nav_button: cn(
              "h-8 w-8 bg-transparent p-0 opacity-60 hover:opacity-100 hover:bg-gray-100 flex items-center justify-center rounded-md transition-colors"
            ),
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "flex",
            head_cell: "text-gray-500 font-semibold rounded-md w-9 font-normal text-[0.75rem] text-center uppercase tracking-wide",
            row: "flex w-full mt-1",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
            day: "h-9 w-9 p-0 font-normal hover:bg-primary-light hover:text-primary rounded-md transition-colors cursor-pointer flex items-center justify-center mx-auto",
            day_selected: "bg-primary text-white hover:bg-primary hover:text-white rounded-md font-bold",
            day_today: "border-2 border-gray-400 font-semibold text-gray-900",
            day_outside: "text-gray-300 opacity-70 hover:bg-transparent hover:text-gray-300",
            day_disabled: "text-gray-200 cursor-not-allowed",
            day_range_start: "bg-primary text-white font-bold rounded-l-md rounded-r-none",
            day_range_end: "bg-primary text-white font-bold rounded-r-md rounded-l-none",
            day_range_middle: "bg-primary-light text-primary rounded-none",
            day_hidden: "invisible",
          }}
          components={{
            IconLeft: () => <ChevronLeft className="h-4 w-4" />,
            IconRight: () => <ChevronRight className="h-4 w-4" />,
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export { DateRangePicker };
